import { useEffect, useState } from "react"
import { liveQuery } from "dexie"
import { db } from "@/db/schema"
import {
  isTotodoFileUrl,
  parseTotodoFileUrl,
} from "@/shared/utils/tiptap-utils"

export interface FileUrlResult {
  url: string
  isLoading: boolean
}

/**
 * Cache of file ID → object URL mappings.
 * Shared across all component instances to avoid redundant Dexie reads.
 * Object URLs are revoked when no longer referenced (via refcount).
 */
const urlCache = new Map<string, { objectUrl: string; refCount: number }>()

/**
 * Resolves a totodo-file:// URL to a renderable blob object URL.
 * Returns the original URL unchanged if it's not a totodo-file:// reference.
 *
 * Uses liveQuery to reactively resolve files that haven't synced yet —
 * when a file arrives via Dexie Cloud sync, the image renders automatically.
 * Returns `isLoading: true` while the file is being fetched so callers
 * can show a placeholder / skeleton.
 */
export function useFileUrl(src: string): FileUrlResult {
  const [result, setResult] = useState<FileUrlResult>(() => {
    if (isTotodoFileUrl(src)) {
      const fileId = parseTotodoFileUrl(src)
      if (fileId) {
        const cached = urlCache.get(fileId)
        if (cached) return { url: cached.objectUrl, isLoading: false }
      }
      return { url: "", isLoading: true }
    }
    return { url: src, isLoading: false }
  })

  useEffect(() => {
    if (!isTotodoFileUrl(src)) {
      setResult({ url: src, isLoading: false })
      return
    }

    const fileId = parseTotodoFileUrl(src)
    if (!fileId) {
      setResult({ url: src, isLoading: false })
      return
    }

    // Check cache first
    const cached = urlCache.get(fileId)
    if (cached) {
      cached.refCount++
      setResult({ url: cached.objectUrl, isLoading: false })
      return () => {
        cached.refCount--
        if (cached.refCount <= 0) {
          URL.revokeObjectURL(cached.objectUrl)
          urlCache.delete(fileId)
        }
      }
    }

    // Signal loading while we wait
    setResult({ url: "", isLoading: true })

    // Use liveQuery to reactively wait for the file (handles sync delay).
    let ownsEntry = false
    const subscription = liveQuery(() => db.files.get(fileId)).subscribe({
      next: (file) => {
        if (!file?.content || ownsEntry) return

        const existing = urlCache.get(fileId)
        if (existing) {
          existing.refCount++
          ownsEntry = true
          setResult({ url: existing.objectUrl, isLoading: false })
          subscription.unsubscribe()
          return
        }

        const blob = new Blob([new Uint8Array(file.content)], { type: file.mimeType })
        const objectUrl = URL.createObjectURL(blob)
        urlCache.set(fileId, { objectUrl, refCount: 1 })
        ownsEntry = true
        setResult({ url: objectUrl, isLoading: false })
        // File content is immutable — no need to keep watching
        subscription.unsubscribe()
      },
      error: (err) => {
        console.error("[useFileUrl] Failed to resolve file:", fileId, err)
        setResult({ url: "", isLoading: false })
      },
    })

    return () => {
      subscription.unsubscribe()
      if (ownsEntry) {
        const entry = urlCache.get(fileId)
        if (entry) {
          entry.refCount--
          if (entry.refCount <= 0) {
            URL.revokeObjectURL(entry.objectUrl)
            urlCache.delete(fileId)
          }
        }
      }
    }
  }, [src])

  return result
}
